import { ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCamera, IconPencil } from '@tabler/icons-react';
import { UserAvatar } from '../UI/UserAvatar';

type EditableAvatarProps = {
  displayName?: string;
  photoURL?: string;
  size: number;
  onClick: () => void;
  /** Accessible name, also the tooltip. */
  label?: string;
  /** What the badge promises: editing the profile, or picking a photo. */
  badge?: 'pencil' | 'camera';
  style?: React.CSSProperties;
};

/**
 * The user's own avatar, as a button that opens the name and photo editor.
 *
 * The pencil badge is always drawn rather than shown on hover: a photo you
 * can tap to change is the convention people arrive with, and on touch there
 * is no hover to reveal it.
 */
export const EditableAvatar = ({
  displayName,
  photoURL,
  size,
  onClick,
  label = 'Edit your name and photo',
  badge = 'pencil',
  style,
}: EditableAvatarProps) => {
  // Badge scales with the avatar, with a floor that stays tappable.
  const badgeSize = Math.max(20, Math.round(size * 0.32));

  return (
    <Tooltip label={label} withArrow>
      <UnstyledButton
        onClick={onClick}
        aria-label={label}
        style={{ position: 'relative', borderRadius: '50%', lineHeight: 0, flexShrink: 0 }}
      >
        <UserAvatar
          displayName={displayName}
          photoURL={photoURL}
          size={size}
          style={style}
        />
        <ThemeIcon
          size={badgeSize}
          radius="xl"
          color="orange"
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            border: '2px solid var(--mantine-color-body)',
          }}
        >
          {badge === 'camera' ? (
            <IconCamera size={Math.round(badgeSize * 0.55)} />
          ) : (
            <IconPencil size={Math.round(badgeSize * 0.55)} />
          )}
        </ThemeIcon>
      </UnstyledButton>
    </Tooltip>
  );
};
